import React, { useCallback, useEffect, useState } from 'react';
import { Button, message, Radio, Space, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import NewAddress from './NewAddress';
import type { AddressItem } from 'types/dto/order';
import './AddressManagement.scss';
import { fetchAddress } from 'services/orderService';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import { MemberProfile } from 'types/dto/profile';
import { useOutletContext } from 'react-router-dom';

interface AddressManagementProps {
  visible: boolean;
  onClose: () => void;
  onAddressSelect?: (selectedAddressId: number) => void;
  selectedAddressId?: number | null;
}

const AddressManagement: React.FC<AddressManagementProps> = ({
  visible,
  onClose,
  onAddressSelect,
  selectedAddressId,
}) => {
  const { t, i18n } = useTranslation('new_order');
  const lang = i18n.language;
  const [selectedAddress, setSelectedAddress] = useState<
    number | null | undefined
  >(selectedAddressId);
  const [isNewAddressVisible, setIsNewAddressVisible] =
    useState<boolean>(false);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  const handleAddressItemClick = (addressId: number) => {
    setSelectedAddress(addressId);
  };

  const handleAddNewAddress = () => {
    setIsNewAddressVisible(true);
  };

  const handleNewAddressBack = () => {
    setIsNewAddressVisible(false);
  };

  const getAddressData = useCallback(async () => {
    try {
      // Refresh addresses list
      const addressDataList = await fetchAddress('/customer', {
        juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
        lang: lang,
      });

      const list = addressDataList || [];
      setAddresses(list);
      return list;
    } catch (err) {
      message.error(t('fetchAddressError'));
    }
  }, [lang, profile?.loyalty_account?.unique_id, t]);

  useEffect(() => {
    if (visible) {
      // Reset internal state when drawer opens
      setSelectedAddress(selectedAddressId);
      setIsNewAddressVisible(false);

      getAddressData();
    }
  }, [visible, selectedAddressId, lang, t, getAddressData]);

  const handleNewAddressSave = async () => {
    const newList = await getAddressData();

    if (newList.length > 0) {
      const latestAddress = newList.reduce((prev: { addressID: number; }, current: { addressID: number; }) =>
        (prev.addressID > current.addressID) ? prev : current
      );
      setSelectedAddress(latestAddress.addressID);
    }
};

  const handleConfirm = async () => {
    if (selectedAddress) {
      onAddressSelect?.(selectedAddress);
      onClose();
    } else {
      console.log('Please select an address');
    }
  };

  return (
    <DrawerFormContainer
      title={t('editAddress') || 'Edit address'}
      description={
        t('editAddressDescription')
      }
      visible={visible}
      onClose={onClose}
      onSave={handleConfirm}
      cancelButtonTitle={t('cancel') || 'Cancel'}
      saveButtonTitle={t('confirm') || 'Confirm'}
    >
      <div className="address-management-content">
        {/* Add New Address Button */}
        <Button
          type="link"
          icon={<PlusOutlined />}
          onClick={handleAddNewAddress}
          className="add-address-button"
          disabled={addresses.length >= 3}
        >
          {t('addNewAddress') || 'Add new address'}
        </Button>

        {/* Address List */}
        <div className="address-list">
          <Radio.Group
            value={selectedAddress}
            className="address-radio-group"
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {addresses.map((address) => (
                <div
                  key={address.addressID}
                  className={`address-item ${
                    selectedAddress === address.addressID ? 'selected' : ''
                  }`}
                  onClick={() => handleAddressItemClick(address.addressID)}
                >
                  <Radio
                    value={address.addressID}
                    className="address-radio"
                  >
                    <div className="address-content">
                      <div className="address-text">
                        <div className="address-line">{address.address}</div>
                      </div>
                      {address.seq === 1 && (
                        <Tag className="default-badge">{t('default')}</Tag>
                      )}
                    </div>
                  </Radio>
                </div>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </div>

      {isNewAddressVisible && (
        <NewAddress
          onClose={onClose}
          onBack={handleNewAddressBack}
          onSave={handleNewAddressSave}
          backButtonText={t('back')}
          showDefaultCheckbox={false}
        />
      )}
    </DrawerFormContainer>
  );
};

export default AddressManagement;
